import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import "./UserCharts.css";
import "chartjs-adapter-date-fns";

function UserCharts({ scores, startDate, endDate }) {
  const chartRef = useRef(null);
  const canvasRef = useRef(null);
  const scatterCanvasRef = useRef(null);
  const scatterChartRef = useRef(null);

  // most recent scores graphed as radar
  useEffect(() => {
    // set as first ones, replace if there are newer
    let pracScore = {};
    let pracMax = {};
    let userScore = {};
    let userMax = {};
    console.log("SCORES", scores);

    if (scores.length === 2) {
      pracScore = scores[0]?.score || {};
      pracMax = scores[0]?.maxScore || {};
      userScore = scores[1]?.score || {};
      userMax = scores[1]?.maxScore || {};
    }
    if (scores.length > 1) {
      for (let i = 0; i < scores.length; i++) {
        if (scores[i]?.quiz === "practitioner") {
          pracScore = scores[i]?.score || {};
          pracMax = scores[i]?.maxScore || {};
        } else if (scores[i]?.quiz === "user") {
          userScore = scores[i]?.score || {};
          userMax = scores[i]?.maxScore || {};
        }
      }
    }

    const data = {
      labels: ["Cognitive", "Affective", "Empathic Response"],
      datasets: [
        {
          label: "Practitioner-to-Practitioner",
          data: [
            pracMax["Cognitive"]
              ? Math.round(
                  (pracScore["Cognitive"] / pracMax["Cognitive"]) * 100
                )
              : 0,
            pracMax["Affective"]
              ? Math.round(
                  (pracScore["Affective"] / pracMax["Affective"]) * 100
                )
              : 0,
            pracMax["Empathic Response"]
              ? Math.round(
                  (pracScore["Empathic Response"] /
                    pracMax["Empathic Response"]) *
                    100
                )
              : 0,
          ],
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderColor: "rgb(75, 192, 192)",
          pointBackgroundColor: "rgb(75, 192, 192)",
          borderWidth: 2,
          fill: true,
        },
        {
          label: "Practitioner-to-User",
          data: [
            userMax["Cognitive"]
              ? Math.round(
                  (userScore["Cognitive"] / userMax["Cognitive"]) * 100
                )
              : 0,
            userMax["Affective"]
              ? Math.round(
                  (userScore["Affective"] / userMax["Affective"]) * 100
                )
              : 0,
            userMax["Empathic Response"]
              ? Math.round(
                  (userScore["Empathic Response"] /
                    userMax["Empathic Response"]) *
                    100
                )
              : 0,
          ],
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          borderColor: "rgb(255, 99, 132)",
          pointBackgroundColor: "rgb(255, 99, 132)",
          borderWidth: 2,
          fill: true,
        },
      ],
    };

    const config = {
      type: "radar",
      data: data,
      options: {
        responsive: true,
        layout: {
          padding: {
            bottom: 0,
          },
        },
        plugins: {
          legend: {
            position: "top",
          },
        },
        scales: {
          r: {
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              stepSize: 20,
            },
          },
        },
      },
    };

    if (chartRef.current) {
      chartRef.current.destroy(); // clean up existing chart
    }

    chartRef.current = new Chart(canvasRef.current, config);
  }, [scores]);

  useEffect(() => {
    const pracScores = [];
    const userScores = [];
    scores.forEach((entry) => {
      const date = new Date(entry.date?.$date || entry.date);
      const s = entry.score;
      const m = entry.maxScore;
      // Defensive: check all keys exist
      if (
        s &&
        m &&
        ["Cognitive", "Affective", "Empathic Response"].every(
          (key) => s[key] !== undefined && m[key] !== undefined && m[key] !== 0
        )
      ) {
        // Normalised total score (0-100)
        const totalScore =
          ((s["Cognitive"] / m["Cognitive"] +
            s["Affective"] / m["Affective"] +
            s["Empathic Response"] / m["Empathic Response"]) /
            3) *
          100;
        const point = {
          x: date,
          y: Math.round(totalScore),
        };
        if (entry.quiz === "practitioner") {
          pracScores.push(point);
        } else if (entry.quiz === "user") {
          userScores.push(point);
        }
      }
    });

    const data = {
      datasets: [
        {
          label: "Practitioner-to-Practitioner",
          data: pracScores,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgb(75, 192, 192)",
          showLine: false,
          tension: 0.3,
        },
        {
          label: "Practitioner-to-User",
          data: userScores,
          backgroundColor: "rgba(255, 99, 132, 0.6)",
          borderColor: "rgb(255, 99, 132)",
          showLine: false,
          tension: 0.3,
        },
      ],
    };

    var startDateObj = null;
    var endDateObj = null;

    if (startDate && startDate !== "") {
      const [year, month, day] = startDate.split("-");
      // Set to 14:00 UTC on the previous day to get 00:00 AEST on the selected date
      const date = new Date(Date.UTC(year, month - 1, day, 14, 0, 0));
      date.setUTCDate(date.getUTCDate() - 1);
      startDateObj = date;
    }

    if (endDate && endDate !== "") {
      const [year, month, day] = endDate.split("-");
      // 11:59:59.999pm AEST is 13:59:59.999 UTC on the selected day
      const date = new Date(Date.UTC(year, month - 1, day, 13, 59, 59, 999));
      endDateObj = date;
    }

    const scatterConfig = {
      type: "scatter",
      data,
      options: {
        responsive: true,
        plugins: {
          title: {
            display: true,
            text: "Total Normalised Scores Over Time",
          },
        },
        scales: {
          x: {
            type: "time",
            time: {
              unit: "day",
              displayFormats: {
                day: "dd/MM",
              },
            },
            title: {
              display: true,
              text: "Date",
            },
            min: startDateObj,
            max: endDateObj,
          },
          y: {
            title: {
              display: true,
              text: "Total Normalised Score (%)",
            },
            suggestedMin: 0,
            suggestedMax: 100,
          },
        },
      },
    };

    if (scatterChartRef.current) {
      scatterChartRef.current.destroy();
    }

    scatterChartRef.current = new Chart(
      scatterCanvasRef.current,
      scatterConfig
    );
  }, [scores, startDate, endDate]);

  const exportScatterToCSV = (data, filename) => {
    const csvRows = [];
    const headers = [
      "Quiz",
      "Date",
      "Total Normalised Score",
      "Cognitive Score",
      "Affective Score",
      "Empathic Response Score",
    ];
    csvRows.push(headers.join(","));

    data.forEach((point) => {
      // point should have quiz, x (date), y (totalScore), and optionally raw/max scores
      const row = [
        point.quiz,
        point.x instanceof Date ? point.x.toISOString() : point.x,
        point.y,
        ((point.cognitiveRaw / point.cognitiveMax) * 100).toFixed(1) ?? "",
        ((point.affectiveRaw / point.affectiveMax) * 100).toFixed(1) ?? "",
        ((point.empathicRaw / point.empathicMax) * 100).toFixed(1) ?? "",
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const exportRadarToCSV = (chart, filename) => {
    if (!chart || !chart.data) return;

    const csvRows = [];
    const headers = [
      "Label",
      ...chart.data.labels.map((label) => `${label} Score`),
    ];
    csvRows.push(headers.join(","));

    chart.data.datasets.forEach((dataset) => {
      const row = [dataset.label, ...dataset.data];
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  // check if user has any existing quiz results
  let noScores = false;

  if (scores != null && scores.length === 0) {
    noScores = true;
  }

  return (
    <div className="user-charts">
      {!noScores && (
        <div className="charts">
          <h3>Most Recent Empathy Scores</h3>
          <canvas ref={canvasRef}></canvas>
          <button
            onClick={() => {
              if (chartRef.current) {
                const link = document.createElement("a");
                link.href = chartRef.current.toBase64Image();
                link.download = "current_scores_chart.png";
                link.click();
              }
            }}
          >
            {" "}
            Download PNG{" "}
          </button>
          <button
            onClick={() => {
              if (chartRef.current) {
                exportRadarToCSV(
                  chartRef.current,
                  "radar_chart_data.csv",
                  scores
                );
              }
            }}
          >
            Export Radar Data to CSV
          </button>
          <h3>Score History</h3>
          <canvas ref={scatterCanvasRef} width={600} height={400}></canvas>
          <button
            onClick={() => {
              if (scatterChartRef.current) {
                const link = document.createElement("a");
                link.href = scatterChartRef.current.toBase64Image();
                link.download = "score_history_chart.png";
                link.click();
              }
            }}
          >
            {" "}
            Download PNG{" "}
          </button>
          <button
            onClick={() => {
              if (scatterChartRef.current) {
                const allData = [];
                scatterChartRef.current.data.datasets.forEach((dataset) => {
                  dataset.data.forEach((point, idx) => {
                    // Find the matching score entry by date and quiz type
                    const scoreEntry = scores.find((entry) => {
                      const entryDate = new Date(
                        entry.date?.$date || entry.date
                      ).toISOString();
                      const pointDate =
                        point.x instanceof Date
                          ? point.x.toISOString()
                          : point.x;
                      return (
                        ((dataset.label === "Practitioner-to-Practitioner" &&
                          entry.quiz === "practitioner") ||
                          (dataset.label === "Practitioner-to-User" &&
                            entry.quiz === "user")) &&
                        entryDate === pointDate
                      );
                    });
                    allData.push({
                      quiz: dataset.label,
                      x: point.x,
                      y: point.y,
                      cognitiveRaw: scoreEntry?.score?.["Cognitive"] ?? "",
                      cognitiveMax: scoreEntry?.maxScore?.["Cognitive"] ?? "",
                      affectiveRaw: scoreEntry?.score?.["Affective"] ?? "",
                      affectiveMax: scoreEntry?.maxScore?.["Affective"] ?? "",
                      empathicRaw:
                        scoreEntry?.score?.["Empathic Response"] ?? "",
                      empathicMax:
                        scoreEntry?.maxScore?.["Empathic Response"] ?? "",
                    });
                  });
                });
                exportScatterToCSV(allData, "scatter_chart_data.csv");
              }
            }}
          >
            Export Scatter Data to CSV
          </button>
        </div>
      )}
    </div>
  );
}

export default UserCharts;
